import type { QuestionnaireUrlState } from './questionnaireSidebarTab.hooks'

export type ImportedQuestionnaireLocation = {
  sectionsList: { sectionId: string; questionsList: { questionId: string }[] }[]
  comments: { id: string; sectionNumber?: number; questionNumber?: number }[]
}

// An imported questionnaire's sections and questions move when it is edited,
// so its URLs name them by id, resolved here to where they are now. A URL
// without ids is read by position.
export const resolveImportedLocation = (
  params: URLSearchParams,
  state: QuestionnaireUrlState,
  form: ImportedQuestionnaireLocation,
): QuestionnaireUrlState => {
  const sectionPosition = (id: string) => {
    const index = form.sectionsList.findIndex(
      (section) => section.sectionId === id,
    )
    return index >= 0 ? index + 1 : undefined
  }
  const questionPosition = (id: string) => {
    for (const [si, section] of form.sectionsList.entries()) {
      const qi = section.questionsList.findIndex(
        (question) => question.questionId === id,
      )
      if (qi >= 0)
        return { commentSectionNumber: si + 1, commentQuestionNumber: qi + 1 }
    }
    return { commentSectionNumber: undefined, commentQuestionNumber: undefined }
  }
  const sectionId = params.get('section_id')
  const result = sectionId
    ? { ...state, sectionNumber: sectionPosition(sectionId) }
    : state
  const commentId = params.get('comment_id')
  if (commentId) {
    const comment = form.comments.find((c) => c.id === commentId)
    return {
      ...result,
      sectionNumber: comment?.sectionNumber || result.sectionNumber,
      commentSectionNumber: comment?.sectionNumber,
      commentQuestionNumber: comment?.questionNumber,
    }
  }
  const questionId = params.get('comment_question_id')
  if (questionId) return { ...result, ...questionPosition(questionId) }
  const commentSectionId = params.get('comment_section_id')
  if (commentSectionId && !state.commentQuestionNumber)
    return {
      ...result,
      commentSectionNumber: sectionPosition(commentSectionId),
      commentQuestionNumber: 0,
    }
  return result
}

export const storeImportedLocation = (
  params: URLSearchParams,
  next: QuestionnaireUrlState,
  form: ImportedQuestionnaireLocation,
): void => {
  const put = (key: string, value?: string) =>
    value ? params.set(key, value) : params.delete(key)
  put('section_id', form.sectionsList[(next.sectionNumber ?? 0) - 1]?.sectionId)
  const section = form.sectionsList[(next.commentSectionNumber ?? 0) - 1]
  put('comment_section_id', section?.sectionId)
  put(
    'comment_question_id',
    section?.questionsList[(next.commentQuestionNumber ?? 0) - 1]?.questionId,
  )
}
